import React, { useState } from 'react';
import { useAlarms, useAlarm, useAcknowledgeAlarm, useResolveAlarm } from '@/api/hooks/useAlarms';
import { AlarmFilters, Alarm } from '@/api/alarms/types';
import { AlarmList } from '@/components/alarms/alarm-list';
import { AlarmDetail } from '@/components/alarms/alarm-detail';
import { AIAnalysis } from '@/components/alarms/ai-analysis';
import { FilterControls } from '@/components/alarms/filter-controls';
import { App } from 'antd';

export function AlarmsPage() {
  const [filters, setFilters] = useState<AlarmFilters>({});
  const [selectedAlarmId, setSelectedAlarmId] = useState<string | null>(null);
  const { message } = App.useApp();

  // Fetch alarms with filters
  const { data: alarmsResponse, isLoading: isAlarmsLoading, refetch: refetchAlarms } = useAlarms(filters);

  // Fetch selected alarm details
  const { data: selectedAlarm, isLoading: isAlarmLoading } = useAlarm(selectedAlarmId);

  // Mutations for alarm actions
  const { mutate: acknowledgeAlarm } = useAcknowledgeAlarm();
  const { mutate: resolveAlarm } = useResolveAlarm();

  const handleSelectAlarm = (alarm: Alarm) => {
    setSelectedAlarmId(alarm.id);
  };

  const handleFilterChange = (newFilters: AlarmFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = () => {
    refetchAlarms();
  };

  const handleAcknowledge = (id: string) => {
    acknowledgeAlarm(
      { id, username: 'Current User' },
      {
        onSuccess: () => {
          message.success('Alarm acknowledged successfully');
        },
        onError: () => {
          message.error('Failed to acknowledge alarm');
        }
      }
    );
  };

  const handleResolve = (id: string) => {
    resolveAlarm(
      { id, username: 'Current User' },
      {
        onSuccess: () => {
          message.success('Alarm resolved successfully');
        },
        onError: () => {
          message.error('Failed to resolve alarm');
        }
      }
    );
  };

  return (
    <div className="h-full" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Alarm List Panel */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '350px',
        borderRight: '1px solid var(--color-light-grey)',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ flexShrink: 0 }}>
          <FilterControls
            filters={filters}
            onFilterChange={handleFilterChange}
            onRefresh={handleRefresh}
          />
        </div>
        <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
          <AlarmList
            alarms={alarmsResponse?.data || []}
            selectedAlarmId={selectedAlarmId}
            onSelectAlarm={handleSelectAlarm}
            isLoading={isAlarmsLoading}
            onRefresh={handleRefresh}
          />
        </div>
      </div>

      {/* Alarm Detail Panel */}
      <div style={{
        position: 'absolute',
        left: '350px',
        top: 0,
        bottom: 0,
        right: '400px',
        borderRight: '1px solid var(--color-light-grey)',
        backgroundColor: 'white'
      }}>
        <AlarmDetail
          alarm={selectedAlarm}
          onAcknowledge={handleAcknowledge}
          onResolve={handleResolve}
          isLoading={isAlarmLoading}
        />
      </div>

      {/* AI Analysis Panel */}
      <div style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '400px',
        backgroundColor: 'white'
      }}>
        <AIAnalysis
          alarm={selectedAlarm}
          alarms={alarmsResponse?.data || []}
        />
      </div>
    </div>
  );
}
